import type { QueryClient } from '@tanstack/react-query'
import { jobDetailQueryKey } from '@/hooks/useJobs'
import { jobsApi } from '@/services/jobs'
import type { ApiResponse, Job, JobDetail } from '@/types'

// Background preload of job details for the agenda, so clicking a job opens the
// detail drawer from cache instead of waiting on GET /jobs/{id}. Rules and the
// reasoning behind every number live in docs/AGENDA_JOB_PANEL_LOADING.md.

// Staging speaks HTTP/1.1 (6 connections per host). 4 leaves 2 free for the
// user's own actions: a click on an unloaded job, drag-and-drop saves, polling.
export const PRELOAD_CONCURRENCY = 4
// A job that comes back on screen is not fetched again while it is this fresh,
// so moving back and forth between dates does not refetch everything.
export const PRELOAD_FRESH_MS = 60_000
// Keep preloaded details well past the default 5 minute gc so a dispatcher who
// leaves the calendar open still gets an instant drawer. The drawer refreshes
// stale data in the background when it opens.
export const PRELOAD_GC_MS = 30 * 60_000
// Wait until the view has been still this long before publishing what is on
// screen, so flipping dates or scrolling triggers nothing for positions passed.
export const PRELOAD_SETTLE_MS = 500
// Cards about to scroll into view start early.
export const PRELOAD_LOOKAHEAD_PX = 200
// Mouse proximity: the nearest few targets within this distance jump the queue.
export const PRELOAD_PROXIMITY_PX = 100
export const PRELOAD_PROXIMITY_COUNT = 3
export const PRELOAD_PROXIMITY_THROTTLE_MS = 100
// Month view: sweeping the mouse across cells opens and closes popovers; only a
// popover the mouse stays on triggers preloading.
export const PRELOAD_POPOVER_DELAY_MS = 150

// DOM contract between the views and useAgendaJobPreload. Any element carrying
// PRELOAD_IDS_ATTR is a preload target; it is watched for visibility when it
// sits inside an element carrying PRELOAD_ROOT_ATTR (the scroll container).
export const PRELOAD_IDS_ATTR = 'data-preload-job-ids'
export const PRELOAD_DATE_ATTR = 'data-preload-date'
export const PRELOAD_TIME_ATTR = 'data-preload-time'
// The job's updated_at as the calendar knows it: a cached detail older than
// this is out of date (rescheduled here, or changed by someone else).
export const PRELOAD_UPDATED_ATTR = 'data-preload-updated'
export const PRELOAD_GROUP_ATTR = 'data-preload-group'
export const PRELOAD_ROOT_ATTR = 'data-preload-root'
// Spread onto a scroll container whose targets should be watched.
export const PRELOAD_ROOT_PROPS = { [PRELOAD_ROOT_ATTR]: '' }

// Props that mark an element showing one job as a preload target.
export function jobPreloadTargetProps(
  job: Pick<Job, 'id' | 'scheduled_date' | 'start_time' | 'updated_at'>,
  group?: 'tray'
) {
  return {
    [PRELOAD_IDS_ATTR]: job.id,
    [PRELOAD_DATE_ATTR]: job.scheduled_date ?? undefined,
    [PRELOAD_TIME_ATTR]: job.start_time ?? undefined,
    [PRELOAD_UPDATED_ATTR]: job.updated_at ?? undefined,
    [PRELOAD_GROUP_ATTR]: group,
  }
}

// Highest priority first. Each source owns an ordered list of job ids; the
// queue is their concatenation, so re-publishing a source reorders the queue.
export const PRELOAD_SOURCES = [
  // Targets the mouse is close to.
  'proximity',
  // Jobs listed in the open month-view popover.
  'popover',
  // Visible cards of the expanded "to plan" tray.
  'tray',
  // First screenful of the tray, from the moment it is expanded.
  'trayOpen',
  // Visible calendar cards (day/week) and today's cells (month).
  'calendar',
  // First screenful of the tray while it is still collapsed.
  'trayCollapsed',
] as const

export type PreloadSource = (typeof PRELOAD_SOURCES)[number]

interface JobDetailPreloadQueueOptions {
  // Resolves when the detail is cached, rejects when the request failed.
  fetchDetail: (jobId: string) => Promise<unknown>
  // The cached detail, or undefined if there is none (never loaded or
  // garbage-collected). `at` is when it was cached, `version` the job's
  // updated_at in that detail (ms).
  cached: (jobId: string) => CachedJobDetail | undefined
  concurrency?: number
  now?: () => number
}

export interface CachedJobDetail {
  at: number
  version: number | undefined
}

export class JobDetailPreloadQueue {
  private readonly sources = new Map<PreloadSource, string[]>()
  private readonly inFlight = new Set<string>()
  private readonly failedAt = new Map<string, number>()
  // Jobs already cached while they have stayed listed. They are not refetched
  // on a timer: a job that remains on screen is loaded once, and the drawer
  // refreshes it when opened. Leaving every source (scroll away, change date)
  // forgets it, so coming back later reloads it if it is no longer fresh.
  private readonly settled = new Set<string>()
  // The newest updated_at the calendar has shown for a job. A cached detail
  // older than that is reloaded even if settled; that is how a reschedule or
  // another user's change reaches the cache without reloading the whole screen.
  private readonly required = new Map<string, number>()
  // The required version a job was last loaded for, so a detail the server
  // keeps returning with an older updated_at is not reloaded in a loop.
  private readonly loadedFor = new Map<string, number>()
  private enabled = false
  private readonly fetchDetail: (jobId: string) => Promise<unknown>
  private readonly cached: (jobId: string) => CachedJobDetail | undefined
  private readonly concurrency: number
  private readonly now: () => number

  constructor({
    fetchDetail,
    cached,
    concurrency = PRELOAD_CONCURRENCY,
    now = Date.now,
  }: JobDetailPreloadQueueOptions) {
    this.fetchDetail = fetchDetail
    this.cached = cached
    this.concurrency = concurrency
    this.now = now
  }

  // Preloading never competes with the calendar request itself: the page
  // enables the queue once the agenda has loaded.
  setEnabled(enabled: boolean) {
    this.enabled = enabled
    this.pump()
  }

  // Replaces what a source wants loaded. Ids dropped here that have not started
  // are simply forgotten; requests already in flight are left to finish.
  // `versions` carries each job's updated_at (ms) as the calendar shows it.
  setSource(
    source: PreloadSource,
    jobIds: readonly string[],
    versions?: ReadonlyMap<string, number>
  ) {
    this.sources.set(source, [...jobIds])
    versions?.forEach((version, jobId) => {
      if (version > (this.required.get(jobId) ?? 0)) {
        this.required.set(jobId, version)
      }
    })
    const listed = new Set([...this.sources.values()].flat())
    for (const perJob of [
      this.settled,
      this.required,
      this.loadedFor,
      this.failedAt,
    ]) {
      for (const jobId of [...perJob.keys()]) {
        if (!listed.has(jobId)) perJob.delete(jobId)
      }
    }
    this.pump()
  }

  clear() {
    this.sources.clear()
    this.settled.clear()
    this.required.clear()
    this.loadedFor.clear()
    this.failedAt.clear()
  }

  private isOutdated(jobId: string, cached: CachedJobDetail): boolean {
    const required = this.required.get(jobId)
    return (
      required !== undefined &&
      cached.version !== undefined &&
      cached.version < required &&
      required > (this.loadedFor.get(jobId) ?? 0)
    )
  }

  private nextJobId(): string | undefined {
    const now = this.now()
    for (const source of PRELOAD_SOURCES) {
      for (const jobId of this.sources.get(source) ?? []) {
        if (this.inFlight.has(jobId)) continue
        const failedAt = this.failedAt.get(jobId)
        if (failedAt !== undefined && now - failedAt < PRELOAD_FRESH_MS) {
          continue
        }
        const cached = this.cached(jobId)
        if (
          cached !== undefined &&
          !this.isOutdated(jobId, cached) &&
          (this.settled.has(jobId) || now - cached.at < PRELOAD_FRESH_MS)
        ) {
          this.settled.add(jobId)
          continue
        }
        return jobId
      }
    }
    return undefined
  }

  private pump() {
    while (this.enabled && this.inFlight.size < this.concurrency) {
      const jobId = this.nextJobId()
      if (jobId === undefined) return
      this.inFlight.add(jobId)
      const requiredAtStart = this.required.get(jobId)
      this.fetchDetail(jobId)
        .then(
          () => {
            this.failedAt.delete(jobId)
            this.settled.add(jobId)
            if (requiredAtStart !== undefined) {
              this.loadedFor.set(jobId, requiredAtStart)
            }
          },
          () => this.failedAt.set(jobId, this.now())
        )
        .finally(() => {
          this.inFlight.delete(jobId)
          this.pump()
        })
    }
  }
}

export function createJobDetailPreloadQueue(queryClient: QueryClient) {
  return new JobDetailPreloadQueue({
    fetchDetail: async jobId => {
      const queryKey = jobDetailQueryKey(jobId)
      const before = queryClient.getQueryState<ApiResponse<JobDetail>>(queryKey)
      try {
        await queryClient.fetchQuery({
          queryKey,
          queryFn: () => jobsApi.get(jobId),
          // The queue has already decided this job needs loading; without
          // this, react-query would hand back a still-fresh cached detail and
          // a job the calendar shows as changed would never be re-downloaded.
          staleTime: 0,
          gcTime: PRELOAD_GC_MS,
          retry: false,
        })
      } catch (error) {
        // A failed preload must not leave an error entry behind: the drawer
        // checks the error before the data. With nothing cached it should open
        // on its loading skeleton; with a detail cached it should keep showing
        // that detail. An entry the drawer is watching is left to it.
        const query = queryClient.getQueryCache().find({ queryKey })
        if (query && query.getObserversCount() === 0) {
          if (before?.data === undefined) {
            queryClient.removeQueries({ queryKey, exact: true })
          } else {
            queryClient.setQueryData(queryKey, before.data, {
              updatedAt: before.dataUpdatedAt,
            })
            if (before.isInvalidated) {
              void queryClient.invalidateQueries({
                queryKey,
                exact: true,
                refetchType: 'none',
              })
            }
          }
        }
        throw error
      }
    },
    // An invalidated detail still counts as cached: broad ['jobs']
    // invalidations (create job, record payment, send report) would otherwise
    // re-download every job on screen. The drawer refetches it on open, and a
    // changed job is caught by its updated_at instead.
    cached: jobId => {
      const state = queryClient.getQueryState<ApiResponse<JobDetail>>(
        jobDetailQueryKey(jobId)
      )
      if (!state || state.data === undefined) return undefined
      return {
        at: state.dataUpdatedAt,
        version: parsePreloadVersion(state.data.data?.updated_at),
      }
    },
  })
}

export interface PreloadTarget {
  jobIds: string[]
  // ISO date (YYYY-MM-DD...) and HH:mm start time of the job(s), when known.
  date: string | null
  time: string | null
  // The job's updated_at (ms) when the target shows a single job.
  version?: number
}

export function parsePreloadVersion(
  updatedAt: string | null | undefined
): number | undefined {
  const version = updatedAt ? Date.parse(updatedAt) : Number.NaN
  return Number.isNaN(version) ? undefined : version
}

// The newest updated_at seen per job across single-job targets.
export function preloadTargetVersions(
  targets: readonly PreloadTarget[]
): Map<string, number> {
  const versions = new Map<string, number>()
  for (const { jobIds, version } of targets) {
    if (version === undefined || jobIds.length !== 1) continue
    versions.set(jobIds[0], Math.max(version, versions.get(jobIds[0]) ?? 0))
  }
  return versions
}

export interface PreloadOrderContext {
  view: 'day' | 'week' | 'month'
  todayIso: string
  nowMinutes: number
}

const UNKNOWN_RANK = Number.MAX_SAFE_INTEGER
const PAST_DAY_OFFSET = 100_000
const MS_PER_DAY = 86_400_000

function utcDay(iso: string): number | null {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return null
  return Date.UTC(year, month - 1, day) / MS_PER_DAY
}

// Today first, then upcoming days nearest first, then past days nearest first.
function dayRank(date: string | null, todayIso: string): number {
  const day = date ? utcDay(date) : null
  const today = utcDay(todayIso)
  if (day === null || today === null) return UNKNOWN_RANK
  const diff = day - today
  return diff >= 0 ? diff : PAST_DAY_OFFSET - diff
}

function minutesOfDay(time: string | null): number | null {
  if (!time) return null
  const [hours, minutes] = time.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
  return hours * 60 + minutes
}

// Day view: jobs nearest the current time of day first (a dispatcher at 10:00
// mostly opens jobs around 10:00). Other views: chronological within a day.
function timeRank(time: string | null, context: PreloadOrderContext): number {
  const minutes = minutesOfDay(time)
  if (minutes === null) return UNKNOWN_RANK
  return context.view === 'day'
    ? Math.abs(minutes - context.nowMinutes)
    : minutes
}

// Orders visible targets and flattens them to unique job ids. A job assigned to
// two technicians has two cards; it is loaded once.
export function orderPreloadTargets(
  targets: readonly PreloadTarget[],
  context: PreloadOrderContext
): string[] {
  const ranked = targets.map((target, index) => ({
    target,
    index,
    day: dayRank(target.date, context.todayIso),
    time: timeRank(target.time, context),
  }))
  ranked.sort((a, b) => a.day - b.day || a.time - b.time || a.index - b.index)
  return uniqueJobIds(ranked.map(({ target }) => target))
}

export function uniqueJobIds(targets: readonly PreloadTarget[]): string[] {
  return [...new Set(targets.flatMap(target => target.jobIds))]
}

export function parsePreloadJobIds(value: string | null | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}
